import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { LobbyistDoc, MemberIndexEntry, Party } from '../types/types';
import { shortPartyName } from '../utils/party';
import { firmLabel, INDEPENDENT_LABEL, registryOnlyPath } from '../components/lobbyists/registry';
import {
  BillListEntry, clientRepresentatives, getAllBills, getAllMembers, getCommitteeList, getLobbyistRegistry, getLobbyists, getLobbyistsOf, getMinistries, getParties,
  getQueries, getRecentBills, getScope, UNTAGGED_KNESSET,
} from './cache';

/* ── Global search: what it searches, how it matches, and the hook the UI uses ──────────
   Everything comes from the shared read models in data/cache.ts, so nothing loads until
   search is used, and a page that already loaded a list doesn't load it again. Bills load
   last and in two steps (the newest 1,000, then the rest), only once there is a query. */

export type SearchKind = 'member' | 'party' | 'committee' | 'ministry' | 'client' | 'bill' | 'lobbyist' | 'query';

// Group order: small, high-signal lists first; bills and queries match many words. Clients
// of lobbyists ("who represents X") come before bills, as in the approved design (3D).
export const SEARCH_KINDS: SearchKind[] = ['member', 'party', 'committee', 'ministry', 'client', 'bill', 'lobbyist', 'query'];

export const SEARCH_KIND_LABEL: Record<SearchKind, string> = {
  member: 'חברי כנסת',
  party: 'סיעות',
  committee: 'ועדות',
  ministry: 'משרדי ממשלה',
  client: 'לקוחות של שדלנים',
  bill: 'הצעות חוק',
  lobbyist: 'שדלנים',
  query: 'שאילתות',
};

// A client of registered lobbyists: who represents it, by firm (pages/SearchPage)
export const clientPath = (clientName: string): string => `/search?client=${encodeURIComponent(clientName)}`;

export const MIN_QUERY_LENGTH = 2;

export interface SearchHit {
  kind: SearchKind;
  id: string;
  to: string;
  title: string;
  subtitle?: string;
  member?: MemberIndexEntry;  // for the avatar
  swatch?: string;            // party color
  stage?: string;             // bill stage
  badge?: string;             // e.g. "סיים/ה כהונה", "הסתיימה"
}

export interface SearchGroup {
  kind: SearchKind;
  hits: SearchHit[];          // every match, best first
}

/* ── Matching ── */

const FINAL_LETTERS: Record<string, string> = { 'ך': 'כ', 'ם': 'מ', 'ן': 'נ', 'ף': 'פ', 'ץ': 'צ' };

// Hebrew-aware: no niqqud or quote marks (ש"ס = שס), punctuation as spaces, final letters as
// regular ones (so a half-typed word still matches), Latin lower-cased
export const normalize = (s: string): string => (s || '')
  .replace(/[֑-ׇ]/g, '')
  .replace(/["'`׳״’”]/g, '')
  .replace(/[-–—־_/\\.,:;()[\]{}!?]/g, ' ')
  .replace(/[ךםןףץ]/g, c => FINAL_LETTERS[c])
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim();

// One-letter Hebrew prefixes (ה, ו, ב, ל, מ, ש, כ): "ליכוד" should find "הליכוד" at a word start
const PREFIXES = ['ה', 'ו', 'ב', 'ל', 'מ', 'ש', 'כ'];

const startsWord = (spaced: string, token: string): boolean => {
  if (spaced.indexOf(' ' + token) >= 0) return true;
  for (let i = 0; i < PREFIXES.length; i++) if (spaced.indexOf(' ' + PREFIXES[i] + token) >= 0) return true;
  return false;
};

// 0 = no match. Every word of the query must appear; matches at the start of the text or of
// its words rank above matches inside a word.
const matchScore = (target: string, q: string, tokens: string[]): number => {
  if (!target) return 0;
  for (let i = 0; i < tokens.length; i++) if (target.indexOf(tokens[i]) < 0) return 0;
  if (target === q) return 100;
  if (target.indexOf(q) === 0) return 85;
  const spaced = ' ' + target;
  if (startsWord(spaced, q)) return 70;
  if (tokens.every(t => startsWord(spaced, t))) return 55;
  return 30;
};

/* ── Sources ── */

interface Entry {
  hit: SearchHit;
  primary: string;    // normalized name or title
  secondary: string;  // normalized extra text (roles, organizations), weighted lower
  rank: number;       // tiebreak among equal scores, lower first
}

const cachedEntries = <T,>(load: () => Promise<T>) => {
  let promise: Promise<T> | null = null;
  return (): Promise<T> => {
    if (!promise) promise = load().catch((err: Error) => { promise = null; throw err; });
    return promise;
  };
};

const partyMap = (parties: Party[]): Record<string, Party> => {
  const map: Record<string, Party> = {};
  parties.forEach(p => { map[p.id] = p; });
  return map;
};

const isPlainMk = (title: string | undefined) => !title || title === 'חבר/ת כנסת' || title === 'חבר כנסת' || title === 'חברת כנסת';

const memberEntries = cachedEntries(async (): Promise<Entry[]> => {
  const [members, parties, scope] = await Promise.all([getAllMembers(), getParties(), getScope()]);
  const byId = partyMap(parties);
  return members.map(m => {
    const party = m.partyId ? byId[m.partyId] : undefined;
    const roles = (m.roles || []).filter(r => !isPlainMk(r.title));
    const primaryRole = roles.find(r => r.isPrimary) || roles[0];
    const departed = m.isCurrentMk === false && !m.governmentMember;
    return {
      hit: {
        kind: 'member' as SearchKind,
        id: m.id,
        to: `/members/${m.id}`,
        title: m.name,
        subtitle: [party ? shortPartyName(party.name) : null, primaryRole ? primaryRole.title : null].filter(Boolean).join(' · ') || undefined,
        member: m,
        // Norwegian Law: a minister of this Knesset who resigned their seat (a minister of an
        // earlier Knesset who wasn't re-elected isn't one)
        badge: departed ? 'סיים/ה כהונה'
          : m.isActiveMk === false && (m.knessetNumber ?? UNTAGGED_KNESSET) === scope.knesset ? 'נורווגי' : undefined,
      },
      primary: normalize(m.name),
      // "שר האוצר" finds the minister
      secondary: normalize(roles.map(r => r.title).join(' ')),
      rank: departed ? 1 : 0,
    };
  });
});

const partyEntries = cachedEntries(async (): Promise<Entry[]> => {
  // Every Knesset's factions; an earlier Knesset's are marked and listed after the current ones
  const [parties, scope] = await Promise.all([getParties(), getScope()]);
  // Knessets whose factions have a coalition (none before a government is sworn in)
  const withCoalition: Record<number, boolean> = {};
  for (const p of parties) if (p.isCoalition === true) withCoalition[p.knessetNumber ?? UNTAGGED_KNESSET] = true;
  return parties.map(p => {
    const name = shortPartyName(p.name || '');
    const k = p.knessetNumber ?? UNTAGGED_KNESSET;
    return {
      hit: {
        kind: 'party' as SearchKind,
        id: p.id,
        to: `/parties/${p.id}`,
        title: name,
        subtitle: withCoalition[k] ? `${p.seats} מנדטים · ${p.isCoalition ? 'קואליציה' : 'אופוזיציה'}` : `${p.seats} מנדטים`,
        swatch: p.color,
        badge: k !== scope.knesset ? `כנסת ${k}` : undefined,
      },
      primary: normalize(name),
      secondary: normalize(`${p.name} ${p.leaderName || ''}`),
      rank: k !== scope.knesset ? 1000 - p.seats : -p.seats,
    };
  });
});

const COMMITTEE_TYPE: Record<string, string> = { standing: 'ועדה קבועה', special: 'ועדה מיוחדת', joint: 'ועדה משותפת' };

const committeeEntries = cachedEntries(async (): Promise<Entry[]> => {
  const committees = await getCommitteeList();
  return committees.map(c => ({
    hit: {
      kind: 'committee' as SearchKind,
      id: c.id,
      to: `/committees/${c.id}`,
      title: c.name,
      subtitle: COMMITTEE_TYPE[c.type] || undefined,
      badge: c.isCurrent === false ? 'הסתיימה' : undefined,
    },
    primary: normalize(c.name),
    secondary: '',
    rank: c.isCurrent === false ? 1 : 0,
  }));
});

const ministryEntries = cachedEntries(async (): Promise<Entry[]> => {
  const [byId, members] = await Promise.all([getMinistries(), getAllMembers()]);
  const names: Record<string, string> = {};
  members.forEach(m => { names[m.id] = m.name; });
  return Object.keys(byId)
    .filter(id => byId[id].isActive !== false)
    .map(id => {
      const m = byId[id];
      const minister = m.currentMinisterId ? names[m.currentMinisterId] : undefined;
      return {
        hit: { kind: 'ministry' as SearchKind, id, to: `/government/ministries/${id}`, title: m.name, subtitle: minister ? `שר/ה: ${minister}` : undefined },
        primary: normalize(m.name),
        secondary: normalize(minister || ''),
        rank: 0,
      };
    });
});

// The lobbyists seen at committee sessions, of every Knesset (a person once: the current
// Knesset's entry, else their latest archived one, marked with its Knesset), then the
// register's lobbyists never seen at one (their page is /lobbyists/reg-{id}). Without the
// register, just the first.
const lobbyistEntries = cachedEntries(async (): Promise<Entry[]> => {
  const scope = await getScope();
  const archived = scope.archivedKnessets.filter(k => k !== scope.knesset).sort((a, b) => b - a);
  const [current, earlier, reg] = await Promise.all([
    getLobbyists(),
    Promise.all(archived.map(k => getLobbyistsOf(k).catch(() => [] as LobbyistDoc[]))),
    getLobbyistRegistry().catch(() => null),
  ]);
  const lobbyists: { l: LobbyistDoc; k: number }[] = current.map(l => ({ l, k: scope.knesset }));
  const seen: Record<string, true> = {};
  for (const l of current) seen[l.id] = true;
  for (let i = 0; i < archived.length; i++) {
    for (const l of earlier[i]) if (!seen[l.id]) { seen[l.id] = true; lobbyists.push({ l, k: archived[i] }); }
  }
  const known: Record<string, true> = {};
  const entries: Entry[] = lobbyists.map(({ l, k }) => {
    known[l.id] = true;
    const firm = reg && Object.prototype.hasOwnProperty.call(reg.byDocId, l.id) ? reg.byDocId[l.id].firm : null;
    return {
      hit: {
        kind: 'lobbyist' as SearchKind,
        id: l.id,
        to: `/lobbyists/${l.id}`,
        title: l.fullName,
        subtitle: [(l.organizations || [])[0], l.totalAppearances ? (l.totalAppearances === 1 ? 'הופעה אחת' : `${l.totalAppearances} הופעות`) : null].filter(Boolean).join(' · ') || undefined,
        badge: k !== scope.knesset ? `כנסת ${k}` : undefined,
      },
      primary: normalize(l.fullName),
      // A firm's name finds its lobbyists
      secondary: normalize((l.organizations || []).concat(firm ? [firm] : []).join(' ')),
      rank: -(l.totalAppearances || 0),
    };
  });
  if (!reg) return entries;
  const ids = Object.keys(reg.lobbyists);
  for (let i = 0; i < ids.length; i++) {
    const r = reg.lobbyists[ids[i]];
    if (r.docId && known[r.docId]) continue;
    entries.push({
      hit: {
        kind: 'lobbyist',
        id: `reg-${r.registryId}`,
        to: registryOnlyPath(r),
        title: r.fullName,
        subtitle: [firmLabel(r), 'רק בפנקס'].filter(Boolean).join(' · '),
      },
      primary: normalize(r.fullName),
      secondary: normalize(r.firm || ''),
      rank: 0, // after everyone with appearances
    });
  }
  return entries;
});

// Every client named in the lobbyist register: "who represents X" (decision 3D). A result
// opens the client's lobbyists by firm.
const clientEntries = cachedEntries(async (): Promise<Entry[]> => {
  const reg = await getLobbyistRegistry();
  if (!reg) return [];
  return Object.keys(reg.clients).map(name => {
    const groups = clientRepresentatives(reg, name);
    let count = 0;
    for (let i = 0; i < groups.length; i++) count += groups[i].lobbyists.length;
    const lead = groups.length > 0 ? groups[0].firm || INDEPENDENT_LABEL : '';
    return {
      hit: {
        kind: 'client' as SearchKind,
        id: name,
        to: clientPath(name),
        title: name,
        subtitle: [
          count === 1 ? 'שדלן/ית אחד/ת' : `${count} שדלנים`,
          lead ? (groups.length > 1 ? `${lead} ועוד ${groups.length - 1}` : lead) : null,
        ].filter(Boolean).join(' · '),
      },
      primary: normalize(name),
      secondary: '',
      rank: -count,
    };
  });
});

const shortDate = (iso: string | undefined | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('he-IL', { day: 'numeric', month: 'short', year: 'numeric' });
};

const queryEntries = cachedEntries(async (): Promise<Entry[]> => {
  const queries = await getQueries();
  return queries.map((q, i) => ({
    hit: {
      kind: 'query' as SearchKind,
      id: q.id,
      to: `/queries/${q.id}`,
      title: q.name,
      subtitle: [q.govMinistryName, shortDate(q.submitDate)].filter(Boolean).join(' · ') || undefined,
    },
    primary: normalize(q.name),
    secondary: '',
    rank: i, // the list is newest first
  }));
});

const toBillEntries = (bills: BillListEntry[]): Entry[] => bills.map((b, i) => ({
  hit: {
    kind: 'bill' as SearchKind,
    id: b.id,
    to: `/legislation/${b.id}`,
    title: b.title,
    subtitle: b.lastUpdated ? `עודכנה ${shortDate(b.lastUpdated)}` : undefined,
    stage: b.simpleStage,
  },
  primary: normalize(b.title),
  secondary: '',
  rank: i, // the list is newest first
}));

const recentBillEntries = cachedEntries(async () => {
  const { bills, complete } = await getRecentBills();
  return { entries: toBillEntries(bills), complete };
});
const allBillEntries = cachedEntries(async () => toBillEntries(await getAllBills()));

const SMALL_SOURCES: { kind: SearchKind; load: () => Promise<Entry[]> }[] = [
  { kind: 'member', load: memberEntries },
  { kind: 'party', load: partyEntries },
  { kind: 'committee', load: committeeEntries },
  { kind: 'ministry', load: ministryEntries },
  { kind: 'client', load: clientEntries },
  { kind: 'lobbyist', load: lobbyistEntries },
  { kind: 'query', load: queryEntries },
];

/* ── The hook ── */

export interface GlobalSearchState {
  query: string;              // the (deferred) query the groups are for
  groups: SearchGroup[];      // only kinds with matches, in SEARCH_KINDS order
  loading: boolean;           // a list is still loading
  billsPartial: boolean;      // bills searched so far are only the newest 1,000
  error: boolean;
}

// `active`: start loading the lists (on focus, or when the search page opens), so results
// are ready by the time the user has typed two letters
export const useGlobalSearch = (rawQuery: string, active: boolean): GlobalSearchState => {
  const query = useDeferredValue(rawQuery);
  const [sources, setSources] = useState<Partial<Record<SearchKind, Entry[]>>>({});
  const [billsComplete, setBillsComplete] = useState(false);
  const [error, setError] = useState(false);
  const started = useRef(false);
  const billsStarted = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    if (!active || started.current) return;
    started.current = true;
    SMALL_SOURCES.forEach(s => {
      s.load()
        .then(entries => { if (alive.current) setSources(prev => ({ ...prev, [s.kind]: entries })); })
        .catch(() => { if (alive.current) setError(true); });
    });
  }, [active]);

  const q = normalize(query);
  const wantsBills = q.length >= MIN_QUERY_LENGTH;
  useEffect(() => {
    if (!wantsBills || billsStarted.current) return;
    billsStarted.current = true;
    recentBillEntries()
      .then(({ entries, complete }) => {
        if (!alive.current) return undefined;
        setSources(prev => (prev.bill && prev.bill.length > entries.length ? prev : { ...prev, bill: entries }));
        if (complete) { setBillsComplete(true); return undefined; }
        return allBillEntries().then(all => {
          if (!alive.current) return;
          setSources(prev => ({ ...prev, bill: all }));
          setBillsComplete(true);
        });
      })
      .catch(() => { if (alive.current) setError(true); });
  }, [wantsBills]);

  const groups = useMemo((): SearchGroup[] => {
    if (q.length < MIN_QUERY_LENGTH) return [];
    const tokens = q.split(' ').filter(Boolean);
    const out: SearchGroup[] = [];
    SEARCH_KINDS.forEach(kind => {
      const entries = sources[kind];
      if (!entries) return;
      const scored: { e: Entry; score: number }[] = [];
      for (let i = 0; i < entries.length; i++) {
        const e = entries[i];
        const score = Math.max(matchScore(e.primary, q, tokens), Math.round(matchScore(e.secondary, q, tokens) * 0.6));
        if (score > 0) scored.push({ e, score });
      }
      if (scored.length === 0) return;
      scored.sort((a, b) => b.score - a.score || a.e.rank - b.e.rank);
      out.push({ kind, hits: scored.map(s => s.e.hit) });
    });
    return out;
  }, [q, sources]);

  const smallLoading = SMALL_SOURCES.some(s => !sources[s.kind]);
  return {
    query,
    groups,
    loading: !error && ((active && smallLoading) || (wantsBills && !sources.bill)),
    billsPartial: !!sources.bill && !billsComplete,
    error,
  };
};

/* ── Recent searches (this browser only) ── */

const RECENT_KEY = 'politikal_recent_searches';
const RECENT_MAX = 8;

export const loadRecentSearches = (): string[] => {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const list = raw ? (JSON.parse(raw) as string[]) : [];
    return Array.isArray(list) ? list.filter(x => typeof x === 'string').slice(0, RECENT_MAX) : [];
  } catch { return []; }
};

export const saveRecentSearch = (query: string): string[] => {
  const q = query.trim();
  if (q.length < MIN_QUERY_LENGTH) return loadRecentSearches();
  const next = [q].concat(loadRecentSearches().filter(x => x !== q)).slice(0, RECENT_MAX);
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* ok */ }
  return next;
};

export const clearRecentSearches = () => {
  try { localStorage.removeItem(RECENT_KEY); } catch { /* ok */ }
};
